import React, { useEffect, useState } from "react";
import {
  validateDate,
  validateForm,
  validateRequired,
  validateSelectField,
  validateTimeString,
  validateTimeStringPattern,
} from "components/common/services/ValidationService";
import Title from "components/common/atoms/Title";
import { t, translate } from "../translation/Translation";
import { ValidationRules } from "components/common/utlis/TypeAnnotations";
import Task from "./Task";
import Button from "components/common/atoms/Button";
import {
  EDIT_FOLLOW_UP,
  FETCH_COMPANY_VACANCIES,
  STORE_FOLLOW_UP,
} from "routes/ApiEndpoints";
import { CENTRAL_DATA_MANAGEMENT_MICROSERVICE } from "Constants";
import CustomNotify from "components/common/atoms/CustomNotify";
import { ApiCall } from "components/common/services/ApiServices";
import CheckBoxField from "components/common/atoms/CheckBoxField";
import { Link, useLocation, useParams } from "react-router-dom";
import { mapToSelect } from "components/common/utlis/MapToSelect";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import { useNavigate } from "react-router-dom";
import FollowUp from "./FollowUp";
import {
  ConfigData,
  FollowUpProps,
  TaskLink,
  TaskProps,
  TodoErrors,
} from "./TaskAnnotations";
import GetQueryParams from "components/common/services/GetQueryParams";
import { selectAuth } from "features/auth/AuthSlice";
import { useSelector } from "react-redux";
import { areRequiredFieldsFilled } from "./CommonSuccessionFunctions";
import {
  getLoopingTypes,
  getLoopingTypeValues,
  isCompletionReasonType,
  isStarterFirstWeekType,
  isStarterStopperType,
  isNewStopperTask,
  isStopperType,
  keepsRegardingOnTypeChange,
} from "./TaskConstants";
import SelectWithSearch from "components/common/atoms/SelectWithSearch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons";
import TextEllipsis from "components/common/atoms/TextEllipsis";
import EditIcon from "static/images/EditIcon";
import ViewIcon from "static/images/ViewIcon";

const Succession: React.FC = () => {
  const getCurrentTime = (): string => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };
  const userAuth = useSelector(selectAuth);

  const getInitialTaskData = (): TaskProps => ({
    id: null,
    office: null,
    date: new Date(),
    time: getCurrentTime(),
    followUpFor: null,
    regarding: null,
    taskType: null,
    info: "",
    hasChild: false,
    vacancy: null,
    isAutoGenerated: false,
    is_parent: false,
    reason: null,
  });

  const getInitialFollowUpData = (): FollowUpProps => ({
    id: null,
    followedBy: null,
    feedback: "",
    date: new Date(),
    time: null,
    completed: false,
    alwaysShow: false,
    duration: null,
    isAutoGenerated: false,
    reason: null,
  });

  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const path = location.pathname;
  const isView = path.includes("view") || false;
  const queryParams = GetQueryParams();
  const close = queryParams.get("close");

  // Single state for all todos - array of TaskLink objects
  const [taskLinks, setTaskLinks] = useState<TaskLink[]>([
    { parentTodo: getInitialTaskData(), childTodo: getInitialFollowUpData() },
  ]);
  const [taskLinksErrors, setTaskLinksErrors] = useState<
    { parentErrors: Partial<TodoErrors>; childErrors: Partial<TodoErrors> }[]
  >([{ parentErrors: {}, childErrors: {} }]);

  const navigate = useNavigate();
  const destination = queryParams.get("destination");

  const [data, setData] = useState<ConfigData>({
    offices: [],
    users: [],
    taskTypes: [],
    taskSubTypes: [],
    durations: [],
    vacancies: [],
    reasons: [],
  });

  // Track which single task is expanded and in which mode ('edit' | 'view')
  const [expandedTask, setExpandedTask] = useState<{
    index: number;
    mode: "edit" | "view";
  } | null>(null);

  const expandTask = (index: number, mode: "edit" | "view") => {
    // Remove unsaved new task (added via "Add next follow-up" but not yet submitted)
    const unsavedIndex = taskLinks.findIndex(
      (link, i) => i > 0 && link.parentTodo.id === null,
    );

    if (unsavedIndex !== -1) {
      setTaskLinks((prev) => prev.filter((_, i) => i !== unsavedIndex));
      setTaskLinksErrors((prev) => prev.filter((_, i) => i !== unsavedIndex));
    }

    const wasExpanded =
      expandedTask?.index === index && expandedTask?.mode === mode;
    setExpandedTask(wasExpanded ? null : { index, mode });
  };

  // On landing in the edit/view flow the list is otherwise fully collapsed. Open
  // the row that renders first (renderOrder[0] === last index) so the user has an
  // editable form straight away; every other row keeps its collapsed state.
  const openTopMostTask = (taskCount: number) => {
    if (id === undefined || taskCount === 0) return;
    const topMostIndex = taskCount - 1;
    setExpandedTask({ index: topMostIndex, mode: isView ? "view" : "edit" });
  };

  const collapseTask = (index: number) => {
    if (expandedTask?.index === index) {
      setExpandedTask(null);
    }
  };

  // In edit flow, all existing linked tasks show as collapsible rows.
  // Newly created (unsaved) tasks stay expanded so the user can fill them in.
  const isCollapsibleRow = (link: TaskLink): boolean => {
    if (id === undefined) return false; // Only in edit flow
    return link.parentTodo.id !== null;
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString("nl-BE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Build the page title for edit flow: "Follow up [FollowUpFor] – [Regarding]"
  const getEditTitle = (): string => {
    if (id === undefined || taskLinks.length === 0) return "";
    const firstTask = taskLinks[0].parentTodo;
    const followUpForLabel = firstTask.followUpFor?.label ?? "";
    const regardingLabel = firstTask.regarding?.label ?? "";
    if (regardingLabel) {
      return `${followUpForLabel} – ${regardingLabel}`;
    }
    return followUpForLabel;
  };

  useEffect(() => {
    fetchTaskDetails();
  }, []);

  const fetchTaskDetails = async () => {
    const requestData = {
      id: id,
      type: queryParams.get("type"),
      typeId: queryParams.get("id"),
      office: queryParams.get("office"),
    };

    const response = await ApiCall.service(
      EDIT_FOLLOW_UP,
      "POST",
      requestData,
      true,
      CENTRAL_DATA_MANAGEMENT_MICROSERVICE,
    );
    if (response?.status === 200) {
      const resData = response?.data;
      // Handle array response (multiple linked tasks in edit flow)
      if (Array.isArray(resData) && resData.length > 0) {
        const firstItem = resData[0];

        // Use config data from first item (same across all items)
        setData({
          offices: mapToSelect(firstItem?.["offices"]),
          users: firstItem?.["users"],
          taskTypes: firstItem?.["taskTypes"],
          taskSubTypes: firstItem?.["taskSubTypes"],
          durations: firstItem?.["durations"],
          vacancies: firstItem?.["vacancies"],
          reasons: firstItem?.["reasons"],
        });
        // Build task links from all items
        const newTaskLinks: TaskLink[] = resData.map(
          (item: any, idx: number) => {
            let parentTodo = { ...getInitialTaskData() };
            let childTodo = { ...getInitialFollowUpData() };

            if (item?.parentTodo) {
              const parentOffice = {
                value: item.parentTodo?.office?.id,
                label: item.parentTodo?.office?.name,
              };
              parentTodo = {
                ...item.parentTodo,
                office: parentOffice,
                // is_parent: idx === 0 ? !!item.parentTodo?.regarding : false,
                is_parent: idx === 0,
              };
            } else {
              parentTodo = {
                ...parentTodo,
                office: item?.["defaultOffice"],
                followUpFor: formatFollowUpFor(item?.navData?.entity),
              };
            }

            if (item?.childTodo) {
              childTodo = { ...item.childTodo };
              if (close === "1") {
                childTodo = { ...childTodo, completed: true };
              }
            }

            const isCompleted = item?.parentTodo?.hasChild
              ? !!item?.childTodo?.completed
              : !!item?.parentTodo?.completed;
            return { parentTodo, childTodo, completed: isCompleted };
          },
        );

        setTaskLinks(newTaskLinks);
        setTaskLinksErrors(
          newTaskLinks.map(() => ({ parentErrors: {}, childErrors: {} })),
        );
        openTopMostTask(newTaskLinks.length);

        // Fetch vacancies for each linked task that needs it
        resData.forEach((item: any) => {
          if (item?.parentTodo) {
            const taskGroup = item.parentTodo?.followUpFor?.group;
            const companyId =
              taskGroup === 2
                ? item.parentTodo?.regarding?.value
                : item.parentTodo?.followUpFor?.value;

            if (
              taskGroup === 1 &&
              item.parentTodo?.taskType?.value !== 10 &&
              companyId
            ) {
              fetchCompanyVacancy(item.parentTodo?.followUpFor?.value);
            }
            if (
              taskGroup === 2 &&
              item.parentTodo?.taskType?.value !== 10 &&
              companyId
            ) {
              fetchCompanyVacancy(item.parentTodo?.regarding?.value);
            }
          }
        });
      } else {
        // Single object response (create flow / single task edit)
        const singleData = Array.isArray(resData) ? resData[0] : resData;

        setData({
          offices: mapToSelect(singleData?.["offices"]),
          users: singleData?.["users"],
          taskTypes: singleData?.["taskTypes"],
          taskSubTypes: singleData?.["taskSubTypes"],
          durations: singleData?.["durations"],
          vacancies: singleData?.["vacancies"],
          reasons: singleData?.["reasons"],
        });
        // Update first taskLink with default office and followUpFor
        setTaskLinks((prev) => {
          const updated = [...prev];
          updated[0] = {
            ...updated[0],
            parentTodo: {
              ...updated[0].parentTodo,
              office: singleData?.["defaultOffice"],
              followUpFor: formatFollowUpFor(singleData?.navData?.entity),
            },
          };
          return updated;
        });

        if (singleData?.parentTodo) {
          const parentOffice = {
            value: singleData?.parentTodo?.office?.id,
            label: singleData?.parentTodo?.office?.name,
          };

          setTaskLinks((prev) => {
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              parentTodo: {
                ...singleData?.parentTodo,
                office: parentOffice,
                // is_parent: !!singleData?.parentTodo?.regarding,
                is_parent: true,
              },
            };
            return updated;
          });

          const taskGroup = singleData?.parentTodo?.followUpFor?.group;
          const companyId =
            taskGroup === 2
              ? singleData?.parentTodo?.regarding?.value
              : singleData?.parentTodo?.followUpFor?.value;

          if (
            taskGroup === 1 &&
            singleData?.parentTodo?.taskType?.value !== 10 &&
            companyId
          ) {
            fetchCompanyVacancy(singleData?.parentTodo?.followUpFor?.value);
          }
          if (
            taskGroup === 2 &&
            singleData?.parentTodo?.taskType?.value !== 10 &&
            companyId
          ) {
            fetchCompanyVacancy(singleData?.parentTodo?.regarding?.value);
          }
        }

        if (singleData?.childTodo) {
          setTaskLinks((prev) => {
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              childTodo: singleData?.childTodo,
            };
            if (close === "1") {
              updated[0] = {
                ...updated[0],
                childTodo: { ...updated[0].childTodo, completed: true },
              };
            }
            return updated;
          });
        }
        openTopMostTask(1);
      }
    } else {
      CustomNotify({
        type: "error",
        message: t("Some error occured"),
      });
    }
  };

  const fetchCompanyVacancy = async (
    companyId: string | number | null | undefined,
  ) => {
    // Never fire the request without an id. The backend answers an id-less request with an
    // unencrypted error body, which fails decryption; ApiCall.service swallows that and
    // returns undefined, which would otherwise surface as a spurious error toast.
    if (companyId === undefined || companyId === null || companyId === "") {
      return;
    }

    const response = await ApiCall.service(
      FETCH_COMPANY_VACANCIES,
      "POST",
      { companyId: companyId },
      true,
      CENTRAL_DATA_MANAGEMENT_MICROSERVICE,
    );

    if (response?.status === 200) {
      setData((prevData) => ({
        ...prevData,
        vacancies: response?.data ?? [],
      }));
    } else {
      CustomNotify({
        type: "error",
        message: t("Some error occured"),
      });
    }
  };

  const formatFollowUpFor = (entity: any) => ({
    value: entity?.value ?? null,
    label: entity?.label ?? "",
    group: getGroupValue(entity?.group),
    bbright_id: entity?.bbright_id,
  });

  type GroupType = "company" | "candidate" | "vacancy" | string;

  const getGroupValue = (group?: GroupType): number | "" => {
    const groupMapping: { [key in GroupType]?: number } = {
      company: 1,
      candidate: 2,
      vacancy: 3,
    };
    return groupMapping[group as GroupType] || "";
  };

  // Validation helper for task
  const getTaskValidationRules = (formData: TaskProps): ValidationRules => ({
    office: [validateSelectField],
    followUpFor: [validateSelectField],
    date: [validateDate],
    regarding:
      formData.taskType?.value === 22 ||
      formData.taskType?.value === 24 ||
      formData.taskType?.value === 23 ||
      formData.taskType?.value === 21
        ? [validateSelectField]
        : [],
    taskType: [validateSelectField],
    time: [validateTimeString, validateTimeStringPattern],
    info: [validateRequired],
    vacancy:
      (formData.taskType?.value === 22 ||
        formData.taskType?.value === 24 ||
        formData.taskType?.value === 23 ||
        formData.taskType?.value === 21) &&
      (formData.regarding?.group == "1" || formData.regarding?.group == "2")
        ? [validateSelectField]
        : [],
    reason: [],
  });

  // Validation helper for follow-up
  const getFollowUpValidationRules = (
    _formData: FollowUpProps,
    _taskTypeValue?: number | null,
  ): ValidationRules => ({
    followedBy: [validateSelectField],
    date: [validateDate],
    time: [validateTimeStringPattern],
  });

  // Handler functions for taskLinks
  const handleTimeChange = (
    name: string,
    value: string,
    index: number,
    type: string,
  ) => {
    setTaskLinks((prev) => {
      const updated = [...prev];
      if (type === "task") {
        updated[index] = {
          ...updated[index],
          parentTodo: { ...updated[index].parentTodo, [name]: value },
        };
      } else if (type === "followUp") {
        updated[index] = {
          ...updated[index],
          childTodo: { ...updated[index].childTodo, [name]: value },
        };
      }
      return updated;
    });
  };

  // Errors are only recalculated on save; clear a field's error as soon as a new value is picked
  // so the message does not stay under a field that is now filled in.
  const clearFieldError = (index: number, type: string, fieldName: string) => {
    const errorKey = type === "task" ? "parentErrors" : "childErrors";
    setTaskLinksErrors((prev) =>
      prev.map((errors, i) =>
        i === index && errors?.[errorKey]?.[fieldName as keyof TodoErrors]
          ? {
              ...errors,
              [errorKey]: { ...errors[errorKey], [fieldName]: undefined },
            }
          : errors,
      ),
    );
  };

  const handleSelectChange = (
    selectedOption: any,
    fieldName: string,
    index: number,
    type: string,
    formData: any,
  ) => {
    clearFieldError(index, type, fieldName);
    setTaskLinks((prev) => {
      const updated = [...prev];
      if (type === "task") {
        if (fieldName === "followUpFor" || fieldName === "taskType") {
          let inheritedRegarding: any = null;
          let inheritedVacancy: any = null;

          if (fieldName === "taskType") {
            const group = updated[index].parentTodo.followUpFor?.group;
            const isLooping = isTaskTypeInLoopingSequence(selectedOption, group);
            const isStarterWeek = isStarterFirstWeekType(selectedOption?.value);

            // "Opstart 1e week" (19/20) keeps a regarding already set on this
            // same task, just like the looping types keep theirs.
            if (isStarterWeek) {
              inheritedRegarding = updated[index].parentTodo.regarding;
              inheritedVacancy = updated[index].parentTodo.vacancy;
            } else if (
              keepsRegardingOnTypeChange(
                updated[index].parentTodo.taskType?.value,
                selectedOption?.value,
              )
            ) {
              // Voorstelling/Selectiegesprek -> "Telefonisch contact" keeps the
              // regarding on this same task. Vacancy is Starter/Stopper-only, so
              // it is still cleared.
              inheritedRegarding = updated[index].parentTodo.regarding;
            }

            // For additional tasks, if the selected taskType is in the looping
            // sequence (or is Opstart 1e week), inherit regarding and vacancy
            // from the first prior task that has regarding.
            if (index > 0 && (isLooping || isStarterWeek) && !inheritedRegarding) {
              for (let i = 0; i < index; i++) {
                if (updated[i].parentTodo.regarding) {
                  inheritedRegarding = updated[i].parentTodo.regarding;
                  inheritedVacancy = updated[i].parentTodo.vacancy;
                  break;
                }
              }
            }

            // The vacancy field only applies to Starter/Stopper; clear it for any other type.
            if (!isStarterStopperType(selectedOption?.value)) {
              inheritedVacancy = null;
            }

            // Fetch vacancies if regarding is inherited
            if (inheritedRegarding) {
              const regardingGroup = inheritedRegarding?.group;
              if (regardingGroup == 1 && selectedOption?.value !== 10) {
                fetchCompanyVacancy(inheritedRegarding?.value);
              }
              if (regardingGroup == 2 && selectedOption?.value !== 10) {
                fetchCompanyVacancy(
                  updated[index].parentTodo.followUpFor?.value,
                );
              }
            }
          }

          updated[index] = {
            ...updated[index],
            parentTodo: {
              ...updated[index].parentTodo,
              taskType: null,
              regarding: inheritedRegarding,
              vacancy: inheritedVacancy,
              [fieldName]: selectedOption,
              // Clear the completion reason when the task type changes, since
              // reasons are filtered by the selected task type.
              ...(fieldName === "taskType" && { reason: null }),
              // For the first task, reset is_parent to false when regarding is cleared
              // ...(index === 0 && { is_parent: false }),
              ...(index === 0 && { is_parent: true }),
            },
          };
        } else if (fieldName === "regarding") {
          updated[index] = {
            ...updated[index],
            parentTodo: {
              ...updated[index].parentTodo,
              [fieldName]: selectedOption,
              vacancy: null,
              // For the first task, set is_parent based on whether regarding has a value
              // ...(index === 0 && { is_parent: !!selectedOption }),
              ...(index === 0 && { is_parent: true }),
            },
          };
          if (
            selectedOption?.group == 1 &&
            updated[index].parentTodo.taskType?.value !== 10
          ) {
            fetchCompanyVacancy(selectedOption?.value);
          }
          if (
            selectedOption?.group == 2 &&
            updated[index].parentTodo.taskType?.value !== 10
          ) {
            fetchCompanyVacancy(formData?.value);
          }
        } else {
          updated[index] = {
            ...updated[index],
            parentTodo: {
              ...updated[index].parentTodo,
              [fieldName]: selectedOption,
            },
          };
        }
      } else if (type === "followUp") {
        updated[index] = {
          ...updated[index],
          childTodo: {
            ...updated[index].childTodo,
            [fieldName]: selectedOption,
          },
        };
      }
      return updated;
    });
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement
    >,
    index: number,
    type: string,
  ) => {
    const { name, value } = e.target;
    setTaskLinks((prev) => {
      const updated = [...prev];
      if (type === "task") {
        updated[index] = {
          ...updated[index],
          parentTodo: { ...updated[index].parentTodo, [name]: value },
        };
      } else if (type === "followUp") {
        updated[index] = {
          ...updated[index],
          childTodo: { ...updated[index].childTodo, [name]: value },
        };
      }
      return updated;
    });
  };

  const handleDateChange = (date: Date | null, index: number, type: string) => {
    setTaskLinks((prev) => {
      const updated = [...prev];
      if (type === "task") {
        updated[index] = {
          ...updated[index],
          parentTodo: { ...updated[index].parentTodo, date: date },
        };
      } else if (type === "followUp") {
        updated[index] = {
          ...updated[index],
          childTodo: { ...updated[index].childTodo, date: date },
        };
      }
      return updated;
    });
  };

  const handleCheckBoxChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    const { name, checked } = event.target as HTMLInputElement;
    setTaskLinks((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        childTodo: { ...updated[index].childTodo, [name]: checked },
      };
      // Clear reason when completed is unchecked. A Stopper keeps its reason: it is required
      // whether or not the follow-up is completed.
      if (
        name === "completed" &&
        !checked &&
        !isStopperType(updated[index].parentTodo.taskType?.value)
      ) {
        updated[index] = {
          ...updated[index],
          childTodo: { ...updated[index].childTodo, reason: null },
        };
      }
      return updated;
    });
  };

  const handleFieldChange = (
    _event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    const user = data?.users.find(
      (item) =>
        item?.value === userAuth.userId &&
        item?.value !== undefined &&
        item?.label !== undefined,
    );

    setTaskLinks((prev) => {
      const updated = [...prev];
      const currentHasChild = updated[index].parentTodo.hasChild;
      updated[index] = {
        ...updated[index],
        parentTodo: {
          ...updated[index].parentTodo,
          hasChild: !currentHasChild,
        },
      };
      if (!currentHasChild) {
        updated[index] = {
          ...updated[index],
          childTodo: {
            id: null,
            followedBy: user?.value
              ? {
                  value: user?.value,
                  label: user?.label ?? "",
                }
              : null,
            feedback: "",
            date: new Date(),
            time: null,
            completed: false,
            alwaysShow: false,
            duration: null,
            isAutoGenerated: false,
            reason: null,
          },
        };
      }
      return updated;
    });
  };

  const handleNextFollowUp = () => {
    const user = data?.users.find(
      (item) =>
        item?.value === userAuth.userId &&
        item?.value !== undefined &&
        item?.label !== undefined,
    );

    // Get the last taskLink to copy from
    const lastTaskLink = taskLinks[taskLinks.length - 1];

    // Determine next task type based on group
    let nextTaskType: any = null;
    const group = lastTaskLink?.parentTodo?.followUpFor?.group;

    const loopingTypes = getLoopingTypes(group);

    // Scan backwards through all tasks to find the last one with a looping type
    // This ensures the sequence continues even if an intermediate task broke it
    let lastLoopingTaskType: any = null;
    if (loopingTypes.length > 0) {
      for (let i = taskLinks.length - 1; i >= 0; i--) {
        const taskType = taskLinks[i]?.parentTodo?.taskType;
        if (
          taskType &&
          loopingTypes.some((lt) => lt.value === taskType.value)
        ) {
          lastLoopingTaskType = taskType;
          break;
        }
      }
    }

    // Find next type in the looping sequence based on the last looping type found
    if (loopingTypes.length > 0 && lastLoopingTaskType) {
      const currentIndex = loopingTypes.findIndex(
        (type) => type.value === lastLoopingTaskType?.value,
      );

      // If found and not the last one, get the next task type
      if (currentIndex !== -1 && currentIndex < loopingTypes.length - 1) {
        nextTaskType = loopingTypes[currentIndex + 1];
      }
    }

    // Find regarding and vacancy from the first prior task that has regarding
    let inheritedRegarding: any = null;
    let inheritedVacancy: any = null;
    for (let i = 0; i < taskLinks.length; i++) {
      if (taskLinks[i].parentTodo.regarding) {
        inheritedRegarding = taskLinks[i].parentTodo.regarding;
        inheritedVacancy = taskLinks[i].parentTodo.vacancy;
        break;
      }
    }

    const newParentTodo: TaskProps = {
      ...lastTaskLink.parentTodo,
      id: null,
      taskType: nextTaskType, // Set next task type in sequence (or null if not in sequence)
      regarding: nextTaskType?.is_multilink ? inheritedRegarding : null, // Prefill regarding from first task with regarding
      vacancy: nextTaskType?.is_multilink ? inheritedVacancy : null, // Prefill vacancy from first task with regarding
      reason: null,
      info: "",
      hasChild: false,
      date: new Date(),
      time: getCurrentTime(),
      is_parent: false,
    };

    const newChildTodo: FollowUpProps = {
      ...getInitialFollowUpData(),
      followedBy: user?.value
        ? {
            value: user?.value,
            label: user?.label ?? "",
          }
        : null,
      date: new Date(),
    };

    // Fetch vacancies for the carried-over regarding entity
    if (nextTaskType?.is_multilink && inheritedRegarding) {
      const regardingGroup = inheritedRegarding?.group;
      const followUpForValue = lastTaskLink.parentTodo.followUpFor?.value;
      if (regardingGroup == 1 && nextTaskType?.value !== 10) {
        fetchCompanyVacancy(inheritedRegarding?.value);
      }
      if (regardingGroup == 2 && nextTaskType?.value !== 10) {
        fetchCompanyVacancy(followUpForValue);
      }
    }

    setTaskLinks((prev) => [
      ...prev,
      { parentTodo: newParentTodo, childTodo: newChildTodo },
    ]);
    setTaskLinksErrors((prev) => [
      ...prev,
      { parentErrors: {}, childErrors: {} },
    ]);
    // Show the new task form only; collapse any previously opened row
    setExpandedTask(null);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let allValid = true;
    const updatedErrors = taskLinks.map((link) => {
      let parentErrors: Partial<TodoErrors> = {};
      let childErrors: Partial<TodoErrors> = {};

      // Validate parent todo
      const taskValidationRules = getTaskValidationRules(link.parentTodo);
      for (const key in link.parentTodo) {
        if (link.parentTodo.hasOwnProperty(key)) {
          const value = link.parentTodo[key as keyof TaskProps];
          const validationErrors = validateForm(
            { ...link.parentTodo, [key]: value },
            taskValidationRules,
          );
          if (validationErrors[key]) {
            parentErrors[key as keyof TodoErrors] = validationErrors[key];
            allValid = false;
          }
        }
      }

      // Validate child todo if hasChild is true
      if (link.parentTodo.hasChild) {
        const followUpValidationRules = getFollowUpValidationRules(
          link.childTodo,
          link.parentTodo.taskType?.value,
        );
        for (const key in link.childTodo) {
          if (link.childTodo.hasOwnProperty(key)) {
            const value = link.childTodo[key as keyof FollowUpProps];
            const validationErrors = validateForm(
              { ...link.childTodo, [key]: value },
              followUpValidationRules,
            );
            if (validationErrors[key]) {
              childErrors[key as keyof TodoErrors] = validationErrors[key];
              allValid = false;
            }
          }
        }
      }

      // A new Stopper requires a completion reason — on its follow-up when it has one,
      // otherwise on the task itself. Existing Stoppers are not checked.
      if (isNewStopperTask(link.parentTodo)) {
        if (link.parentTodo.hasChild && !link.childTodo.reason) {
          childErrors.reason = t("This field is required");
          allValid = false;
        } else if (!link.parentTodo.hasChild && !link.parentTodo.reason) {
          parentErrors.reason = t("This field is required");
          allValid = false;
        }
      }

      return { parentErrors, childErrors };
    });

    setTaskLinksErrors(updatedErrors);
    if (!allValid) {
      // Every task of the group is validated, but collapsed rows are not rendered, so an error on
      // one of them would be invisible and Submit would seem to do nothing. Open the first task
      // with an error and tell the user what to fix.
      const firstInvalidIndex = updatedErrors.findIndex(
        (errors) =>
          Object.values(errors.parentErrors).some(Boolean) ||
          Object.values(errors.childErrors).some(Boolean),
      );
      if (firstInvalidIndex !== -1 && expandedTask?.index !== firstInvalidIndex) {
        setExpandedTask({ index: firstInvalidIndex, mode: "edit" });
      }
      CustomNotify({
        type: "error",
        message: t("Please fill in all required fields"),
      });
    }
    if (allValid) {
      // Prepare payload as array of objects
      const followUps = taskLinks.map((link) => ({
        parentTodo: {
          ...link.parentTodo,
          reason: link.parentTodo.hasChild ? null : link.parentTodo.reason,
        },
        childTodo: link.parentTodo.hasChild
          ? { ...link.childTodo, reason: link.childTodo.reason ?? null }
          : null,
      }));

      const response = await ApiCall.service(
        STORE_FOLLOW_UP,
        "POST",
        followUps,
        true,
        CENTRAL_DATA_MANAGEMENT_MICROSERVICE,
      );

      if (response?.status === 200) {
        CustomNotify({ type: "success", message: t(response?.msg) });
        navigate(destination ?? "/manage/follow-up");
      } else {
        CustomNotify({ type: "error", message: t("Something went wrong") });
      }
    }
  };

  // Helper function to check if taskType is in loopingTypes for a given group
  const isTaskTypeInLoopingSequence = (taskType: any, group: any): boolean => {
    if (!taskType || !group) return false;
    return getLoopingTypeValues(group).includes(taskType?.value);
  };

  // True when any prior task already has a regarding value to inherit from
  const hasPriorRegarding = (currentIndex: number): boolean => {
    for (let i = 0; i < currentIndex; i++) {
      if (taskLinks[i]?.parentTodo?.regarding) {
        return true;
      }
    }
    return false;
  };

  // Stable render order (newest-created first). The expanded task surfaces via
  // the form section above the table; the table itself never reshuffles on edit.
  const renderOrder = taskLinks.map((_, i) => i).reverse();

  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Follow-up",
          read: true,
        },
      ]}
      renderNoAccess={true}
    >
      <div>
        {/* Page title with entity name in edit flow */}
        {id !== undefined && getEditTitle() && (
          <div className="mb-4">
            <span style={{ fontSize: "1.1vw", fontWeight: 600 }}>
              {getEditTitle()}
            </span>
          </div>
        )}
        <Title
          title={
            isView
              ? t("View follow up")
              : id === undefined
                ? t("New action")
                : t("Edit action")
          }
        />
        <form onSubmit={(e) => handleSubmit(e)}>
          {/* Actions live above the scroll area so Submit and Next Follow Up stay
              reachable without scrolling past a long follow-up chain */}
          {!isView && (
            <div>
              {taskLinks.length >= 1 &&
                areRequiredFieldsFilled(taskLinks[taskLinks.length - 1]) && (
                  <Button
                    title={t("Next action")}
                    type="button"
                    handleClick={handleNextFollowUp}
                    className="form-button shadow-none float-end me-3"
                  />
                )}
              {expandedTask?.mode !== "view" && (
                <Button
                  title={t("Submit")}
                  type="submit"
                  handleClick={(e) => handleSubmit(e)}
                  className="form-button shadow-none float-end me-3"
                />
              )}
              <div className="clearfix"></div>
            </div>
          )}
          <div
            className="scrollBarDesign"
            style={{
              height: "calc(100vh - 16vw)",
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {/* Expanded/form items */}
            {renderOrder.map((index) => {
              const link = taskLinks[index];
              const collapsible = isCollapsibleRow(link);
              const expandedMode =
                expandedTask?.index === index ? expandedTask.mode : undefined;
              const isExpanded = expandedTask?.index === index;
              const isRowViewMode = expandedMode === "view" || isView;

              if (collapsible && !isExpanded) return null;

              return (
                <div className="row mb-4" key={index}>
                  <div className="col-12">
                    <div className="form-border">
                      {/* Collapse header when expanded */}
                      {collapsible && (
                        <div
                          className="d-flex align-items-center justify-content-between mb-3 pb-2"
                          style={{ borderBottom: "1px solid #dee2e6" }}
                        >
                          <div
                            className="d-flex align-items-center cursor-pointer"
                            onClick={() => collapseTask(index)}
                            style={{ cursor: "pointer" }}
                          >
                            <FontAwesomeIcon
                              icon={faAngleDown}
                              className="me-2"
                            />
                            <span className="fw-bold">
                              {t(`${link.parentTodo.taskType?.label ?? ""}`)}
                            </span>
                            <span className="ms-2 text-muted">
                              - {link.parentTodo.followUpFor?.label ?? ""}
                            </span>
                          </div>
                          {expandedMode === "view" && (
                            <span className="badge bg-secondary">
                              {t("View mode")}
                            </span>
                          )}
                        </div>
                      )}
                      {/* Task */}
                      <Task
                        data={data}
                        formData={link.parentTodo}
                        handleChange={(
                          e: React.ChangeEvent<
                            | HTMLSelectElement
                            | HTMLInputElement
                            | HTMLTextAreaElement
                          >,
                          type: string,
                        ) => handleChange(e, index, type)}
                        handleSelectChange={(
                          option: any,
                          fieldName: string,
                          type: string,
                          formData: any,
                        ) =>
                          handleSelectChange(
                            option,
                            fieldName,
                            index,
                            type,
                            formData,
                          )
                        }
                        errors={taskLinksErrors[index]?.parentErrors ?? {}}
                        handleTimeChange={(
                          name: string,
                          value: string,
                          type: string,
                        ) => handleTimeChange(name, value, index, type)}
                        handleDateChange={(date: Date | null, type: string) =>
                          handleDateChange(date, index, type)
                        }
                        type="task"
                        isView={isRowViewMode}
                        isAdditionalTask={
                          index > 0 &&
                          isTaskTypeInLoopingSequence(
                            link.parentTodo.taskType,
                            link.parentTodo.followUpFor?.group,
                          )
                        }
                        // disableRegarding={
                        //   index > 0 &&
                        //   isTaskTypeInLoopingSequence(
                        //     link.parentTodo.taskType,
                        //     link.parentTodo.followUpFor?.group,
                        //   ) &&
                        //   hasPriorRegarding(index)
                        // }
                        // isEditParent={index === 0 && id !== undefined}
                      />
                      {!link.parentTodo.hasChild &&
                        isCompletionReasonType(
                          link.parentTodo.taskType?.value,
                        ) && (
                          <div>
                            <SelectWithSearch
                              title={t("Completion Reason")}
                              isMandatory={isNewStopperTask(link.parentTodo)}
                              search={true}
                              options={
                                data.reasons.filter(
                                  (reason) =>
                                    reason?.task_type_id ===
                                    link.parentTodo.taskType?.value,
                                ) ?? []
                              }
                              onChange={(e) =>
                                handleSelectChange(
                                  e,
                                  "reason",
                                  index,
                                  "task",
                                  link.parentTodo?.followUpFor,
                                )
                              }
                              isMulti={false}
                              name="reason"
                              value={link.parentTodo.reason}
                              error={
                                taskLinksErrors[index]?.parentErrors.reason
                              }
                              isDisabled={isRowViewMode}
                              isTranslate={true}
                            />
                          </div>
                        )}
                      {/* Add follow up checkbox */}
                      <div className="mt-3">
                        <CheckBoxField
                          label={t("Add todo")}
                          name="hasChild"
                          onChangeHandler={(
                            e: React.ChangeEvent<
                              HTMLInputElement | HTMLTextAreaElement
                            >,
                          ) => handleFieldChange(e, index)}
                          isChecked={link.parentTodo.hasChild}
                          className="document-checkbox"
                          disable={
                            isRowViewMode || link.parentTodo?.isAutoGenerated
                              ? true
                              : false
                          }
                          id={`addFollowUp-${index}`}
                          lineHeight="1vw"
                        />
                      </div>
                      {/* Follow up */}
                      {link.parentTodo.hasChild && (
                        <div
                          className="mt-3 pt-3"
                          style={{ borderTop: "1px solid #dee2e6" }}
                        >
                          <FollowUp
                            data={data}
                            formData={link.childTodo}
                            handleChange={(
                              e: React.ChangeEvent<
                                | HTMLSelectElement
                                | HTMLInputElement
                                | HTMLTextAreaElement
                              >,
                              type: string,
                            ) => handleChange(e, index, type)}
                            handleSelectChange={(
                              option: any,
                              fieldName: string,
                              type: string,
                            ) =>
                              handleSelectChange(
                                option,
                                fieldName,
                                index,
                                type,
                                null,
                              )
                            }
                            errors={taskLinksErrors[index]?.childErrors ?? {}}
                            handleTimeChange={(
                              name: string,
                              value: string,
                              type: string,
                            ) => handleTimeChange(name, value, index, type)}
                            handleDateChange={(
                              date: Date | null,
                              type: string,
                            ) => handleDateChange(date, index, type)}
                            type="followUp"
                            handleCheckBoxChange={(
                              e: React.ChangeEvent<
                                HTMLInputElement | HTMLTextAreaElement
                              >,
                            ) => handleCheckBoxChange(e, index)}
                            isView={isRowViewMode}
                            {...(isCompletionReasonType(
                              link.parentTodo.taskType?.value,
                            )
                              ? {
                                  reasonOptions:
                                    data.reasons.filter(
                                      (reason) =>
                                        reason?.task_type_id ===
                                        link.parentTodo.taskType?.value,
                                    ) ?? [],
                                  reasonValue: link.childTodo.reason,
                                  reasonError:
                                    taskLinksErrors[index]?.childErrors.reason,
                                  isReasonMandatory: isNewStopperTask(link.parentTodo),
                                  onReasonChange: (e: any) =>
                                    handleSelectChange(
                                      e,
                                      "reason",
                                      index,
                                      "followUp",
                                      link.parentTodo?.followUpFor,
                                    ),
                                }
                              : {})}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Collapsed rows - single table */}
            {renderOrder.some((index) =>
              isCollapsibleRow(taskLinks[index]),
            ) && (
              <div className="position-relative tableMainWrapper">
                <div className={`table-responsive tableSection`}>
                  <table className="table manageCandidates borderless mb-0">
                    <tbody>
                      {renderOrder.map((index) => {
                        const link = taskLinks[index];
                        if (!isCollapsibleRow(link)) return null;
                        // Drive the status off the persisted backend status
                        // (link.completed) so it only flips after submit, not
                        // on local edits. For special (completion-reason) task
                        // types, additionally require a reason to be present.
                        const isOpen = link.parentTodo.hasChild
                          ? !link.completed
                          : isCompletionReasonType(
                                link.parentTodo.taskType?.value,
                              )
                            ? !link.completed || !link.parentTodo.reason
                            : !link.completed;
                        return (
                          <tr key={index} className="align-middle">
                            <td className="text-break">
                              <TextEllipsis
                                label={link.parentTodo.office?.label ?? ""}
                                title={link.parentTodo.office?.label ?? ""}
                                width="4.5vw"
                              />
                            </td>
                            <td className="text-break">
                              <TextEllipsis
                                label={formatDate(link.parentTodo.date)}
                                title={formatDate(link.parentTodo.date)}
                                width="5vw"
                              />
                            </td>
                            <td className="text-break">
                              <TextEllipsis
                                label={link.parentTodo.followUpFor?.label ?? ""}
                                title={link.parentTodo.followUpFor?.label ?? ""}
                                width="5.5vw"
                              />
                            </td>
                            <td className="text-break">
                              <TextEllipsis
                                label={t(
                                  `${link.parentTodo.taskType?.label ?? ""}`,
                                )}
                                title={t(
                                  `${link.parentTodo.taskType?.label ?? ""}`,
                                )}
                                width="4.5vw"
                              />
                            </td>
                            <td data-label={t("Status")}>
                              <span
                                className={`d-inline-block rounded-circle status-icon ${
                                  isOpen ? "bg-warning" : "bg-success"
                                }`}
                                data-toggle="tooltip"
                                style={{ marginLeft: "0.8vw" }}
                                title={t(isOpen ? "Open" : "Completed")}
                              />
                            </td>
                            <td className="text-break">
                              <TextEllipsis
                                label={link.parentTodo.regarding?.label ?? ""}
                                title={link.parentTodo.regarding?.label ?? ""}
                                width="4.5vw"
                              />
                            </td>
                            <td className="text-break">
                              <TextEllipsis
                                label={link.parentTodo.info ?? ""}
                                title={link.parentTodo.info ?? ""}
                                width="4.5vw"
                              />
                            </td>
                            <td className="text-break">
                              <TextEllipsis
                                label={link.childTodo.followedBy?.label ?? ""}
                                title={link.childTodo.followedBy?.label ?? ""}
                                width="4.5vw"
                              />
                            </td>
                            <td className="table-action-icons">
                              {!isView && (
                                <span
                                  title={t("Edit")}
                                  onClick={() => expandTask(index, "edit")}
                                  className="table-action-icons cursor-pointer marginRightPoint5 rounded-0 shadow-none"
                                >
                                  <EditIcon />
                                </span>
                              )}
                              <span
                                title={t("View")}
                                onClick={() => expandTask(index, "view")}
                                className="table-action-icons cursor-pointer rounded-0 shadow-none"
                              >
                                <ViewIcon />
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <div className="row backPadding">
            <div className="col-6 align-self-center">
              <Link
                to={destination ?? "/manage/follow-up"}
                className=" back-btn text-decoration-underline"
              >
                {t("Back")}
              </Link>
            </div>
          </div>
        </form>
      </div>
    </AccessControl>
  );
};
export default translate(Succession);
